import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, Trash2, AlertCircle, Info, CheckCircle, X } from "lucide-react";
import PopupTransition from "@/components/ui/PopupTransition";

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "danger" | "warning" | "info" | "success";
  isLoading?: boolean;
  icon?: React.ReactNode;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Επιβεβαίωση",
  cancelText = "Ακύρωση",
  variant = "danger",
  isLoading = false,
  icon,
}) => {
  const getVariantStyles = () => {
    switch (variant) {
      case "danger":
        return {
          iconBg: "bg-red-100",
          iconColor: "text-red-600",
          defaultIcon: <Trash2 className="h-6 w-6" />,
          confirmButton: "bg-red-600 hover:bg-red-700 focus:ring-red-500",
          confirmButtonText: "text-white",
        };
      case "warning":
        return {
          iconBg: "bg-yellow-100",
          iconColor: "text-yellow-600",
          defaultIcon: <AlertTriangle className="h-6 w-6" />,
          confirmButton: "bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500",
          confirmButtonText: "text-white",
        };
      case "info":
        return {
          iconBg: "bg-blue-100",
          iconColor: "text-blue-600",
          defaultIcon: <Info className="h-6 w-6" />,
          confirmButton: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500",
          confirmButtonText: "text-white",
        };
      case "success":
        return {
          iconBg: "bg-green-100",
          iconColor: "text-green-600",
          defaultIcon: <CheckCircle className="h-6 w-6" />,
          confirmButton: "bg-green-600 hover:bg-green-700 focus:ring-green-500",
          confirmButtonText: "text-white",
        };
      default:
        return {
          iconBg: "bg-red-100",
          iconColor: "text-red-600",
          defaultIcon: <AlertCircle className="h-6 w-6" />,
          confirmButton: "bg-red-600 hover:bg-red-700 focus:ring-red-500",
          confirmButtonText: "text-white",
        };
    }
  };

  const styles = getVariantStyles();

  const containerVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 20 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        duration: 0.4,
        bounce: 0.2,
        staggerChildren: 0.1,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      y: 10,
      transition: { duration: 0.2 },
    },
  } as const;

  const childVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  } as const;

  const handleConfirm = () => {
    if (!isLoading) {
      onConfirm();
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <PopupTransition open={isOpen} onClose={handleClose}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Subtle background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50/50 via-white to-gray-50/30 rounded-3xl" />

        {/* Content */}
        <div className="relative z-10">
          {/* Header with Icon */}
          <motion.div
            variants={childVariants}
            className="flex items-start gap-4 mb-6"
          >
            <motion.div
              className={`flex items-center justify-center w-12 h-12 ${styles.iconBg} rounded-2xl shadow-sm`}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 500, delay: 0.1 }}
            >
              <div className={styles.iconColor}>
                {icon || styles.defaultIcon}
              </div>
            </motion.div>

            <div className="flex-1">
              <motion.h3
                variants={childVariants}
                className="text-xl font-bold text-gray-900 mb-2"
              >
                {title}
              </motion.h3>
              <motion.p
                variants={childVariants}
                className="text-gray-600 leading-relaxed"
              >
                {message}
              </motion.p>
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            variants={childVariants}
            className="flex gap-3 mt-8"
          >
            <motion.button
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1 px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {cancelText}
            </motion.button>

            <motion.button
              onClick={handleConfirm}
              disabled={isLoading}
              className={`flex-1 px-6 py-3 ${styles.confirmButton} ${styles.confirmButtonText} rounded-xl transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2`}
              whileHover={{
                scale: 1.02,
                boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.15)",
              }}
              whileTap={{ scale: 0.98 }}
            >
              <AnimatePresence mode="wait">
                {isLoading ? (
                  <motion.div
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center gap-2"
                  >
                    <motion.div
                      className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    />
                    Επεξεργασία...
                  </motion.div>
                ) : (
                  <motion.div
                    key="confirm"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center gap-2"
                  >
                    {variant === "danger" && <Trash2 className="h-4 w-4" />}
                    {confirmText}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </motion.div>
        </div>
      </motion.div>
    </PopupTransition>
  );
};

export default ConfirmationModal;